import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Modal, Button, Input } from '../common';
import { joinGameByCode } from '../../api/games';

export const JoinGameModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors },
  } = useForm();

  // Auto-focus the invite code input when modal opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure modal is rendered
      const timer = setTimeout(() => {
        setFocus('inviteCode');
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, setFocus]);

  const onSubmit = async (data) => {
    setError('');
    setLoading(true);

    try {
      const code = data.inviteCode.toUpperCase();
      const response = await joinGameByCode(code);

      // Invalidate games cache to show new game in dashboard
      queryClient.invalidateQueries({ queryKey: ['games'] });

      reset();
      onClose();
      navigate(`/games/${response.game.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to join game');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setError('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Join a Game" size="sm">
      {error && (
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Invite Code"
          {...register('inviteCode', {
            required: 'Invite code is required',
            minLength: { value: 6, message: 'Code must be 6 characters' },
            maxLength: { value: 6, message: 'Code must be 6 characters' },
          })}
          error={errors.inviteCode?.message}
          placeholder="ABC123"
          className="text-center text-2xl tracking-widest uppercase"
          maxLength={6}
        />

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" loading={loading}>
            Join Game
          </Button>
        </div>
      </form>
    </Modal>
  );
};
